import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Id } from 'convex/_generated/dataModel';
import { GardenPlannerContainer } from '@modules/garden-planner/containers/garden_planner_container';
import { gardenPlannerWebViews } from '@modules/garden-planner/views/web';
import type { ConfirmRequest } from '@modules/garden-planner/models';
import { useGardenPlannerStore } from '@shared/store/useGardenPlannerStore';
import { useUIStore } from '@/src/store/useUIStore';

export function GardenDetailScreen() {
  const navigate = useNavigate();
  const { gardenid } = useParams<{ gardenid: string }>();
  const gardenId = gardenid as Id<'gardens'>;

  const onNavigate = useMemo(
    () => (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  const alerts = useUIStore((state) => state.alerts);
  const removeAlert = useUIStore((state) => state.removeAlert);
  const setConfirmDialog = useUIStore((state) => state.setConfirmDialog);
  const setCopySuccess = useGardenPlannerStore((state) => state.setCopySuccess);

  const onNavigateBack = useCallback(() => {
    onNavigate('/gardens');
  }, [onNavigate]);

  const onConfirm = useCallback(
    (request: ConfirmRequest) => {
      setConfirmDialog({
        isOpen: true,
        title: request.title,
        message: request.message,
        onConfirm: async () => {
          await request.onConfirm();
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        },
      });
    },
    [setConfirmDialog]
  );

  const onShareCopy = useCallback(async () => {
    const shareUrl = `${window.location.origin}/gardens/${gardenId}/share`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy share link:', err);
    }
  }, [gardenId, setCopySuccess]);

  return (
    <GardenPlannerContainer
      views={gardenPlannerWebViews}
      gardenId={gardenId}
      alerts={alerts}
      onDismissAlert={removeAlert}
      onNavigateBack={onNavigateBack}
      onConfirm={onConfirm}
      onShareCopy={onShareCopy}
    />
  );
}
